import { Inject, Injectable } from '@angular/core';
import { Select } from '@ngxs/store';
import {
  canBookWithInsurance,
  CLIENT_APP_ID,
  ClientAppId,
  Dossier,
  getCoverMessage,
  getCoverRangeMessage,
  getInsuredMessage,
  isCovered,
  isInsured,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DossierState } from '../../store';

@Injectable({
  providedIn: 'root',
})
export class InsuranceCoverSandboxService {
  @Select(DossierState)
  private readonly dossier$: Observable<Dossier>;

  private readonly isInsuranceKnown$: Observable<boolean>;
  private readonly isInsured$: Observable<boolean>;
  private readonly canBookWithInsurance$: Observable<boolean>;
  private readonly isCovered$: Observable<boolean>;
  private readonly insuredMessage$: Observable<string>;
  private readonly coverMessage$: Observable<string>;
  private readonly coverRangeMessage$: Observable<string | null>;

  constructor(
    private readonly translator: TranslatorService,
    private readonly featureFlipManager: FeatureFlippingManagerService,
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId
  ) {
    const allGlassesCovered = this.featureFlipManager.isFeatureActivated(
      'display-insurance-cover-for-all-glass'
    );

    const defaultIsInsuredWithoutGlass =
      this.featureFlipManager.isFeatureActivated(
        this.clientAppId === 'customer'
          ? 'b2c-default-display-insured-without-glass'
          : 'b2b-default-display-insured-without-glass'
      );

    const disableBookWithoutInsurance =
      this.featureFlipManager.isFeatureActivated(
        this.clientAppId === 'customer'
          ? 'b2c-disable-booking-without-insurance'
          : 'b2b-disable-booking-without-insurance'
      );

    this.isInsuranceKnown$ = this.dossier$.pipe(
      map((dossier) => {
        const insurance = isInsured(dossier);
        return insurance === true || insurance.notInsuredReason !== 'unknown';
      })
    );

    this.isInsured$ = this.dossier$.pipe(
      map((dossier) => isInsured(dossier) === true)
    );

    this.canBookWithInsurance$ = this.dossier$.pipe(
      map(
        (dossier) =>
          canBookWithInsurance(
            dossier,
            {
              coverAllGlasses: allGlassesCovered,
              defaultIsInsuredWithoutGlass,
            },
            disableBookWithoutInsurance
          ) === true
      )
    );

    this.isCovered$ = this.dossier$.pipe(
      map(
        (dossier) =>
          isCovered(dossier, {
            coverAllGlasses: allGlassesCovered,
            defaultIsInsuredWithoutGlass,
          }) === true
      )
    );

    this.insuredMessage$ = this.dossier$.pipe(
      map((dossier) =>
        getInsuredMessage(
          dossier,
          { coverAllGlasses: allGlassesCovered, defaultIsInsuredWithoutGlass },
          clientAppId
        )
      ),
      switchMap((message) => this.translator.getTranslation(message.key))
    );

    this.coverMessage$ = this.dossier$.pipe(
      map((dossier) =>
        getCoverMessage(
          dossier,
          { coverAllGlasses: allGlassesCovered, defaultIsInsuredWithoutGlass },
          this.clientAppId
        )
      ),
      switchMap((keyParams) =>
        this.translator.getTranslation(keyParams.key, keyParams.params)
      )
    );

    this.coverRangeMessage$ = this.dossier$.pipe(
      map((dossier) =>
        getCoverRangeMessage(
          dossier,
          { coverAllGlasses: allGlassesCovered, defaultIsInsuredWithoutGlass },
          this.clientAppId
        )
      ),
      switchMap((trad) => {
        if (!trad) {
          return of(null);
        }

        return this.translator.getTranslation(trad.key, trad.params);
      })
    );
  }

  getIsInsuranceKnown() {
    return this.isInsuranceKnown$;
  }

  getIsInsured() {
    return this.isInsured$;
  }

  getCanBookWithInsurance() {
    return this.canBookWithInsurance$;
  }

  getIsCovered() {
    return this.isCovered$;
  }

  getInsuredMessage() {
    return this.insuredMessage$;
  }

  getCoverMessage() {
    return this.coverMessage$;
  }

  getCoverRangeMessage() {
    return this.coverRangeMessage$;
  }
}
